<template>

  <Filters :active="active" :filters="filters" @filter="handleClick"/>

  <!-- role="img" plus a label is what makes the canvas mean anything to a screen
       reader: a bare <canvas> exposes nothing at all, so the entire index was
       silent. The role also stops assistive tech descending into the canvas, so
       the readable copy of the series lives in the table below, outside it. -->
  <div class="chart-canvas" v-if="chartData" role="img" :aria-label="chartSummary">
    <Chart :labels="chartLabels" :data="chartData"/>
  </div>

  <!-- The series in text. Visually hidden, but it is real markup: it gives
       keyboard and screen-reader users the actual numbers, and it makes the index
       machine-readable for search engines and anything citing the figure, which
       for an index product is worth more than the compliance on its own. -->
  <table class="sr-only" v-if="series.length">
    <caption>Fusca Strong Wool Indicator, {{ rangeLabel }}. Price in NZ dollars per kg clean.</caption>
    <thead>
      <tr>
        <th scope="col">Sale date</th>
        <th scope="col">Price (NZ$/kg clean)</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="point in series" :key="point.date">
        <th scope="row">{{ point.date }}</th>
        <td>${{ point.price }}</td>
      </tr>
    </tbody>
  </table>

  <p class="chart-meta" v-if="lastUpdated">
    Last updated {{ lastUpdated }} &middot; sourced from NZ wool auction results
  </p>
  <div class="flex items-end gap-1" v-if="logo">
    <span class="text-xs text-grey-600">Powered by</span>
    <a href="https://www.fusca.co.nz" style="text-decoration: none;" target="_blank" title="Go to Fusca">
      <img src="../img/logo-black.png" alt="Fusca" class="w-[70px]" width="70"/>
    </a>
  </div>

</template>

<script>
import Filters from "./components/Filters";
import Chart from "./components/Chart";
import {ref, computed, onMounted} from 'vue';
import axios from 'axios';

/* API dates arrive as YYYY-MM-DD; everything user-facing on this site is
 * day/month/year. */
const toNzDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default {
  components: {
    Filters,
    Chart
  },
  props: {
    logo: {
      default: false
    }
  },
  setup() {
    /* 1Y by default. 10Y answers a different question and most visitors want
     * "what has it done lately"; the longer ranges are one click away. */
    const active = ref({unit: "years", value: 1})
    const filters = new Map([
      ['1M', {unit: "months", value: 1}],
      ['3M', {unit: "months", value: 3}],
      ['6M', {unit: "months", value: 6}],
      ['9M', {unit: "months", value: 9}],
      ['1Y', {unit: "years", value: 1}],
      ['3Y', {unit: "years", value: 3}],
      ['10Y', {unit: "years", value: 10}],
      /* The API validates unit in days|weeks|months|years with value >= 1, so
       * "all" is expressed as a range wide enough to reach the first record
       * (July 2010) rather than needing a new endpoint. */
      ['All', {unit: "years", value: 100}],
    ])
    const chartLabels = ref(null)
    const chartData = ref(null)
    const lastUpdated = ref(null)

    /* Cents to dollars, matching Chart.vue. Both read the same API field, so if
     * one converted and the other did not the chart and its description would
     * quietly disagree. */
    const dollars = computed(() => {
      return chartData.value ? Array.from(chartData.value).map(v => v / 100) : [];
    })

    const series = computed(() => {
      if (!chartLabels.value || !dollars.value.length) return [];
      return chartLabels.value.map((label, i) => ({
        date: toNzDate(label),
        price: dollars.value[i].toFixed(2),
      }));
    })

    const rangeLabel = computed(() => {
      if (!chartLabels.value || !chartLabels.value.length) return '';
      return `${toNzDate(chartLabels.value[0])} to ${toNzDate(chartLabels.value[chartLabels.value.length - 1])}`;
    })

    const chartSummary = computed(() => {
      const values = dollars.value;
      if (!values.length) return '';

      const high = Math.max(...values).toFixed(2);
      const low = Math.min(...values).toFixed(2);
      const latest = values[values.length - 1].toFixed(2);

      return `Line chart of the Fusca Strong Wool Indicator, ${rangeLabel.value}, `
          + `${values.length} sales. Latest $${latest} per kg clean. `
          + `High $${high}, low $${low}.`;
    })

    const handleClick = (e) => {
      active.value = e
      getData();
    }

    // get data from api
    const getData = async () => {
      await axios.post('/strong-wool-index/historic',
          {
            unit: active.value.unit,
            value: active.value.value
          }
      ).then((response) => {
        // get the keys from the response and set them as the chart labels
        chartLabels.value = Object.keys(response.data)
        // get the values from the response and set them as the chart data
        chartData.value = Object.values(response.data)

        // Freshness claim belongs on the page, not implied by the copy.
        const keys = Object.keys(response.data)
        if (keys.length) {
          lastUpdated.value = toNzDate(keys[keys.length - 1])
        }
      })
    }

    // call getData on mount
    onMounted(async () => {
      await getData()
    })

    return {
      active,
      filters,
      chartLabels,
      chartData,
      lastUpdated,
      series,
      rangeLabel,
      chartSummary,
      handleClick
    }

  }
}
</script>
